import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { investorAPI, fileAPI } from '../lib/api';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import StartupPortfolioCard from '../components/StartupPortfolioCard';
import ProgressBar from '../components/ProgressBar';

const InvestorDashboard = () => {
	const { user, logout } = useAuth();
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState('portfolio');
	const [files, setFiles] = useState([]);
	const [fileTab, setFileTab] = useState('received');
	const [loading, setLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedFile, setSelectedFile] = useState(null);
	const [showFileModal, setShowFileModal] = useState(false);
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [selectedUploadFile, setSelectedUploadFile] = useState(null);
	const [tags, setTags] = useState([]);
	const [newTag, setNewTag] = useState('');
	const [uploadProgress, setUploadProgress] = useState(0);
	const [dragActive, setDragActive] = useState(false);
	const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });
	const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'warning' });
	const [startups, setStartups] = useState([]);
	const [investments, setInvestments] = useState([]);
	const [events, setEvents] = useState([]);
	const [loadingStartups, setLoadingStartups] = useState(false);
	const [loadingEvents, setLoadingEvents] = useState(false);
	const [showProfileMenu, setShowProfileMenu] = useState(false);
	const profileMenuRef = useRef(null);

	// Close profile menu when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
				setShowProfileMenu(false);
			}
		};

		if (showProfileMenu) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [showProfileMenu]);

	useEffect(() => {
		if (user?.id) {
			loadFiles();
			loadPortfolio();
			loadEvents();
		}
	}, [user?.id]);

	const loadFiles = async () => {
		setLoading(true);
		try {
			const [sharedFiles, myFiles] = await Promise.all([
				investorAPI.getSharedFiles(),
				fileAPI.getMyFiles()
			]);

			// Merge and deduplicate files based on ID
			const allFiles = [...sharedFiles, ...myFiles];
			const uniqueFiles = Array.from(new Map(allFiles.map(file => [file.id, file])).values());

			setFiles(uniqueFiles.map(file => ({ ...file, tags: file.tags || [] })));
		} catch (err) {
			console.error('Failed to load files:', err);
			setAlert({ isOpen: true, title: 'Error', message: 'Failed to load files', type: 'error' });
		} finally {
			setLoading(false);
		}
	};

	const loadPortfolio = async () => {
		if (!user?.id) return;
		setLoadingStartups(true);
		try {
			const portfolioData = await investorAPI.getPortfolio(user.id);
			setStartups(portfolioData || []);
			// Extract investment details if available
			const investmentData = portfolioData?.map(p => ({
				startupId: p.id,
				investmentAmount: p.investmentAmount,
				equityPercentage: p.equityPercentage,
				investmentDate: p.investmentDate,
				investmentStage: p.investmentStage,
				status: p.investmentStatus || 'ACTIVE',
				currency: p.currency || 'INR'
			})) || [];
			setInvestments(investmentData);
		} catch (err) {
			console.error('Failed to load portfolio:', err);
			setStartups([]);
			setInvestments([]);
		} finally {
			setLoadingStartups(false);
		}
	};
	const loadEvents = async () => {
		if (!user?.id) return;
		setLoadingEvents(true);
		try {
			const eventsData = await investorAPI.getEvents(user.id);
			setEvents(eventsData || []);
		} catch (err) {
			console.error('Failed to load events:', err);
			setEvents([]);
		} finally {
			setLoadingEvents(false);
		}
	};

	const previewFile = async (file) => {
		try {
			const response = await fileAPI.getFilePreview(file.id);
			window.open(response.previewUrl, '_blank');
		} catch (err) {
			setAlert({ isOpen: true, title: 'Error', message: 'Failed to preview file', type: 'error' });
		}
	};

	const downloadFile = async (file) => {
		try {
			const response = await fileAPI.getFileDownload(file.id);

			// Create a blob from the response data
			const blob = new Blob([response.data], { type: response.headers['content-type'] });

			// Create a link element and trigger the download
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;

			// Try to get filename from content-disposition header
			let filename = file.originalName || 'download';
			const contentDisposition = response.headers['content-disposition'];
			if (contentDisposition) {
				const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
				if (filenameMatch && filenameMatch.length === 2) {
					filename = filenameMatch[1];
				}
			}

			link.download = filename;
			document.body.appendChild(link);
			link.click();

			// Clean up
			window.URL.revokeObjectURL(url);
			document.body.removeChild(link);
		} catch (err) {
			console.error('Download error:', err);
			setAlert({ isOpen: true, title: 'Error', message: 'Failed to download file', type: 'error' });
		}
	};

	const handleFileUpload = async (file) => {
		if (!file) return;

		setUploading(true);
		setUploadProgress(0);
		try {
			const formData = new FormData();
			formData.append('file', file);
			if (tags.length > 0) {
				tags.forEach(tag => formData.append('tags', tag));
			}

			await fileAPI.uploadFileForInvestor(formData, (progress) => {
				setUploadProgress(progress);
			});
			setUploadProgress(100);
			// specific delay to show 100% completion
			await new Promise(resolve => setTimeout(resolve, 1000));

			await loadFiles();
			setShowUploadModal(false);
			setSelectedUploadFile(null);
			setTags([]);
		} catch (err) {
			setAlert({ isOpen: true, title: 'Error', message: err.response?.data?.error || 'Failed to upload file', type: 'error' });
		} finally {
			setUploading(false);
			setUploadProgress(0);
		}
	};

	const handleDrag = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true);
		} else if (e.type === 'dragleave') {
			setDragActive(false);
		}
	};

	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);

		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			setSelectedUploadFile(e.dataTransfer.files[0]);
		}
	};

	const addTag = () => {
		if (newTag.trim() && !tags.includes(newTag.trim())) {
			setTags([...tags, newTag.trim()]);
			setNewTag('');
		}
	};

	const removeTag = (tagToRemove) => {
		setTags(tags.filter(tag => tag !== tagToRemove));
	};

	const getFileMetadata = async (file) => {
		try {
			const response = await fileAPI.getFileMetadata(file.id);
			setSelectedFile({ ...file, ...response });
			setShowFileModal(true);
		} catch (err) {
			setAlert({ isOpen: true, title: 'Error', message: 'Failed to get file metadata', type: 'error' });
		}
	};

	const handleLogout = () => {
		logout();
		navigate('/login');
	};

	const filteredFiles = files.filter(file => {
		const matchesSearch = file.originalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
			file.uploadedByName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
			file.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

		if (!matchesSearch) return false;

		if (fileTab === 'sent') {
			return file.uploadedById === user?.id;
		} else {
			// Received files are those NOT uploaded by me
			return file.uploadedById !== user?.id;
		}
	});

	const formatDateTime = (dateString) => {
		if (!dateString) return 'N/A';
		return new Date(dateString).toLocaleString('en-IN', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	const getFileIcon = (contentType) => {
		if (!contentType) return 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z';

		if (contentType.includes('pdf')) return 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z';
		if (contentType.includes('word') || contentType.includes('document')) return 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z';
		if (contentType.includes('excel') || contentType.includes('spreadsheet')) return 'M9 17v1a1 1 0 001 1h4a1 1 0 001-1v-1m3-2V8a2 2 0 00-2-2H9a2 2 0 00-2 2v7m3-2h6';
		if (contentType.includes('powerpoint') || contentType.includes('presentation')) return 'M7 4v16M17 4v16M3 8h4m10 0h4M3 16h4m10 0h4';
		if (contentType.includes('image')) return 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z';
		return 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z';
	};

	const deleteFile = (file) => {
		setConfirm({
			isOpen: true,
			title: 'Delete File',
			message: `Are you sure you want to delete "${file.originalName}"? This will permanently delete the file and remove it from all shared views.`,
			type: 'danger',
			onConfirm: async () => {
				await performDeleteFile(file.id);
			}
		});
	};

	const performDeleteFile = async (fileId) => {
		try {
			await fileAPI.deleteFile(fileId);
			setAlert({ isOpen: true, title: 'Success', message: 'File deleted successfully', type: 'success' });
			loadFiles();
		} catch (err) {
			console.error('Delete error:', err);
			setAlert({ isOpen: true, title: 'Error', message: err.message || 'Failed to delete file', type: 'error' });
		} finally {
			setConfirm({ ...confirm, isOpen: false });
		}
	};

	const formatCurrency = (amount, currency = 'INR') => {
		if (!amount) return '-';
		return new Intl.NumberFormat('en-IN', {
			style: 'currency',
			currency: currency,
			maximumFractionDigits: 0
		}).format(amount);
	};

	// Calculate stats
	const totalInvested = investments.reduce((sum, inv) => sum + (inv.investmentAmount || 0), 0);
	const portfolioCount = startups.length;
	const documentsCount = files.length;
	const eventsCount = events.length;

	const sidebarItems = [
		{ id: 'portfolio', label: 'My Portfolio', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
		{ id: 'documents', label: 'Documents', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
		{ id: 'events', label: 'Events', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
	];

	const getInvestmentForStartup = (startupId) => {
		return investments.find(inv => inv.startupId === startupId) || null;
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex">
			{/* Fixed Sidebar */}
			<aside className="w-72 bg-white border-r border-gray-200 flex flex-col fixed h-full shadow-sm">
				{/* Logo Section */}
				<div className="p-6 border-b border-gray-100">
					<div className="flex items-center space-x-3">
						<div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/20">
							<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
						</div>
						<div>
							<h1 className="text-xl font-bold text-gray-900">iVault</h1>
							<p className="text-xs text-gray-500">Investor Portal</p>
						</div>
					</div>
				</div>

				{/* User Info */}
				<div className="p-4 mx-4 mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
					<div className="flex items-center space-x-3">
						<div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md">
							<span className="text-white font-semibold">
								{user?.name?.charAt(0)?.toUpperCase()}
							</span>
						</div>
						<div className="flex-1 min-w-0">
							<p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
							<p className="text-xs text-gray-500 truncate">{user?.email}</p>
						</div>
					</div>
				</div>

				{/* Navigation */}
				<nav className="flex-1 p-4 space-y-1 overflow-y-auto">
					{sidebarItems.map((item) => (
						<button
							key={item.id}
							onClick={() => setActiveTab(item.id)}
							className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${activeTab === item.id
								? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-600/30'
								: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
								}`}
						>
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
							</svg>
							<span className="font-medium">{item.label}</span>
						</button>
					))}
				</nav>

				{/* Bottom Actions */}
				<div className="p-4 border-t border-gray-100 space-y-2">
					<button
						onClick={() => navigate(`/${user?.tenantIdentifier || 'default'}/my-subscription`)}
						className="w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
					>
						<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
						</svg>
						<span className="text-sm font-medium">Subscription</span>
					</button>
					<button
						onClick={() => navigate(`/${user?.tenantIdentifier || 'default'}/billing`)}
						className="w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
					>
						<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
						</svg>
						<span className="text-sm font-medium">Billing</span>
					</button>
					<button
						onClick={handleLogout}
						className="w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
					>
						<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
						</svg>
						<span className="text-sm font-medium">Logout</span>
					</button>
				</div>
			</aside>

			{/* Main Content Area */}
			<main className="flex-1 ml-72 min-h-screen">
				{/* Header */}
				<header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-10">
					<div className="px-8 py-4">
						<div className="flex items-center justify-between">
							<div>
								<h2 className="text-2xl font-bold text-gray-900">
									{activeTab === 'portfolio' && 'My Portfolio'}
									{activeTab === 'documents' && 'Shared Documents'}
									{activeTab === 'events' && 'Events'}
								</h2>
								<p className="text-sm text-gray-500 mt-1">
									{activeTab === 'portfolio' && 'Track your investments and portfolio performance'}
									{activeTab === 'documents' && 'Documents shared by your portfolio companies'}
									{activeTab === 'events' && 'Events where your portfolio companies are participating'}
								</p>
							</div>
							{activeTab === 'documents' && (
								<button
									onClick={() => setShowUploadModal(true)}
									className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-blue-600/30 transition-all duration-200 flex items-center space-x-2"
								>
									<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
									</svg>
									<span>Upload</span>
								</button>
							)}
						</div>
					</div>
				</header>

				{/* Stats Cards */}
				<div className="px-8 py-6">
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
						{/* Total Invested */}
						<div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
							<div className="flex items-center justify-between">
								<div>
									<p className="text-sm font-medium text-gray-500">Total Invested</p>
									<p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(totalInvested)}</p>
								</div>
								<div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/30">
									<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
									</svg>
								</div>
							</div>
							<div className="mt-4 flex items-center text-sm">
								<span className="text-green-600 font-medium flex items-center">
									<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
									</svg>
									Active Investments
								</span>
							</div>
						</div>

						{/* Portfolio Companies */}
						<div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
							<div className="flex items-center justify-between">
								<div>
									<p className="text-sm font-medium text-gray-500">Portfolio Companies</p>
									<p className="text-2xl font-bold text-gray-900 mt-1">{portfolioCount}</p>
								</div>
								<div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
									<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
									</svg>
								</div>
							</div>
							<div className="mt-4 flex items-center text-sm text-gray-500">
								Startups you've invested in
							</div>
						</div>

						{/* Documents */}
						<div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
							<div className="flex items-center justify-between">
								<div>
									<p className="text-sm font-medium text-gray-500">Documents</p>
									<p className="text-2xl font-bold text-gray-900 mt-1">{documentsCount}</p>
								</div>
								<div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-violet-500 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/30">
									<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
									</svg>
								</div>
							</div>
							<div className="mt-4 flex items-center text-sm text-gray-500">
								Shared with you
							</div>
						</div>

						{/* Events */}
						<div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
							<div className="flex items-center justify-between">
								<div>
									<p className="text-sm font-medium text-gray-500">Events</p>
									<p className="text-2xl font-bold text-gray-900 mt-1">{eventsCount}</p>
								</div>
								<div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/30">
									<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
									</svg>
								</div>
							</div>
							<div className="mt-4 flex items-center text-sm text-gray-500">
								Related events
							</div>
						</div>
					</div>
				</div>

				{/* Tab Content */}
				<div className="px-8 pb-8">
					{/* Portfolio Tab */}
					{activeTab === 'portfolio' && (
						<div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
							<div className="p-6 border-b border-gray-100">
								<div className="flex items-center justify-between">
									<div>
										<h3 className="text-lg font-semibold text-gray-900">Portfolio Startups</h3>
										<p className="text-sm text-gray-500 mt-1">Startups you have invested in</p>
									</div>
									<div className="relative">
										<input
											type="text"
											placeholder="Search portfolio..."
											value={searchTerm}
											onChange={(e) => setSearchTerm(e.target.value)}
											className="w-64 pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
										/>
										<svg className="absolute left-3 top-3 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
										</svg>
									</div>
								</div>
							</div>
							<div className="p-6">
								{loadingStartups ? (
									<div className="text-center py-16">
										<div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
											<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
										</div>
										<p className="text-lg font-medium text-gray-900">Loading portfolio...</p>
									</div>
								) : startups.length === 0 ? (
									<div className="text-center py-16">
										<div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
											<svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
											</svg>
										</div>
										<h3 className="text-lg font-semibold text-gray-900 mb-2">No portfolio startups yet</h3>
										<p className="text-gray-500">Startups you invest in will appear here</p>
									</div>
								) : (
									<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
										{startups
											.filter(startup =>
												(startup.organizationName || startup.name || '')
													.toLowerCase()
													.includes(searchTerm.toLowerCase())
											)
											.map((startup) => (
												<StartupPortfolioCard
													key={startup.id}
													startup={startup}
													investment={getInvestmentForStartup(startup.id)}
													showInvestmentDetails={true}
												/>
											))}
									</div>
								)}
							</div>
						</div>
					)}

					{/* Documents Tab */}
					{activeTab === 'documents' && (
						<div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
							<div className="p-6 border-b border-gray-100">
								<div className="flex items-center justify-between">
									<div>
										<h3 className="text-lg font-semibold text-gray-900">
											{fileTab === 'received' ? 'Shared Documents' : 'My Documents'}
										</h3>
										<p className="text-sm text-gray-500 mt-1">
											{fileTab === 'received' ? 'Documents shared with you by startups' : 'Documents uploaded by you'}
										</p>
									</div>
									<div className="relative">
										<input
											type="text"
											placeholder="Search documents..."
											value={searchTerm}
											onChange={(e) => setSearchTerm(e.target.value)}
											className="w-64 pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
										/>
										<svg className="absolute left-3 top-3 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
										</svg>
									</div>
								</div>

								{/* Sub-tabs for Documents */}
								<div className="mt-6 border-b border-gray-200">
									<nav className="flex -mb-px space-x-8">
										<button
											onClick={() => setFileTab('received')}
											className={`${fileTab === 'received'
												? 'border-blue-500 text-blue-600'
												: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
												} whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm`}
										>
											Received Files
										</button>
										<button
											onClick={() => setFileTab('sent')}
											className={`${fileTab === 'sent'
												? 'border-blue-500 text-blue-600'
												: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
												} whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm`}
										>
											Sent Files
										</button>
									</nav>
								</div>
							</div>
							<div className="p-6">
								{loading ? (
									<div className="text-center py-16">
										<div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
											<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
										</div>
										<p className="text-lg font-medium text-gray-900">Loading documents...</p>
									</div>
								) : filteredFiles.length === 0 ? (
									<div className="text-center py-16">
										<div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
											<svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
											</svg>
										</div>
										<h3 className="text-lg font-semibold text-gray-900 mb-2">
											{searchTerm
												? 'No documents found'
												: fileTab === 'received'
													? 'No shared documents yet'
													: 'No uploaded documents yet'}
										</h3>
										<p className="text-gray-500">
											{searchTerm
												? 'Try adjusting your search terms'
												: fileTab === 'received'
													? 'Startups will share documents with you here'
													: 'Documents you upload will appear here'}
										</p>
									</div>
								) : (
									<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
										{filteredFiles.map((file) => (
											<div key={file.id} className="group bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg hover:border-blue-300 transition-all duration-300">
												<div className="flex items-start justify-between mb-4">
													<div className="flex items-center flex-1 min-w-0">
														<div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center mr-3 group-hover:from-blue-200 group-hover:to-indigo-200 transition-colors flex-shrink-0">
															<svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={getFileIcon(file.contentType)} />
															</svg>
														</div>
														<div className="flex-1 min-w-0">
															<h3
																className="text-sm font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors cursor-help"
																title={file.originalName}
															>
																{file.originalName}
															</h3>
															{/* Metadata hidden as per request 
															<p className="text-xs text-gray-500">
																{file.fileSize ? `${(file.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
															</p>
															*/}
														</div>
													</div>
												</div>

												{file.tags && file.tags.length > 0 && (
													<div className="flex flex-wrap gap-1 mb-3">
														{file.tags.slice(0, 3).map((tag, index) => (
															<span key={index} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
																{tag}
															</span>
														))}
														{file.tags.length > 3 && (
															<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
																+{file.tags.length - 3}
															</span>
														)}
													</div>
												)}

												<div className="flex items-center justify-between pt-3 border-t border-gray-100">
													<div className="flex items-center text-xs text-gray-500">
														<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
														</svg>
														{formatDateTime(file.uploadedAt)}
													</div>
													<div className="flex space-x-1">
														<button
															onClick={() => previewFile(file)}
															className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all"
															title="Preview"
														>
															<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
															</svg>
														</button>
														<button
															onClick={() => downloadFile(file)}
															className="p-2 text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-all"
															title="Download"
														>
															<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
															</svg>
														</button>
														<button
															onClick={() => getFileMetadata(file)}
															className="p-2 text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all"
															title="Details"
														>
															<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
															</svg>
														</button>
														{/* Delete button - only for owner */}
														{(file.uploadedById === user?.id) && (
															<button
																onClick={(e) => {
																	e.stopPropagation();
																	deleteFile(file);
																}}
																className="p-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-all"
																title="Delete"
															>
																<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
																</svg>
															</button>
														)}
													</div>
												</div>
											</div>
										))}
									</div>
								)}
							</div>
						</div>
					)}

					{/* Events Tab */}
					{activeTab === 'events' && (
						<div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
							<div className="p-6 border-b border-gray-100">
								<div>
									<h3 className="text-lg font-semibold text-gray-900">Related Events</h3>
									<p className="text-sm text-gray-500 mt-1">Events where your portfolio startups are participating</p>
								</div>
							</div>
							<div className="p-6">
								{loadingEvents ? (
									<div className="text-center py-16">
										<div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
											<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
										</div>
										<p className="text-lg font-medium text-gray-900">Loading events...</p>
									</div>
								) : events.length === 0 ? (
									<div className="text-center py-16">
										<div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
											<svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
											</svg>
										</div>
										<h3 className="text-lg font-semibold text-gray-900 mb-2">No related events</h3>
										<p className="text-gray-500">Events where your portfolio startups participate will appear here</p>
									</div>
								) : (
									<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
										{events.map((event) => (
											<div
												key={event.id}
												onClick={() => navigate(`/default/events/${event.id}`)}
												className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-blue-300 transition-all duration-300 cursor-pointer"
											>
												{/* Event Header */}
												<div className="h-24 bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 relative">
													<div className="absolute inset-0 bg-black/10"></div>
													<div className="absolute bottom-4 left-4 right-4">
														<h3 className="text-white font-bold truncate text-lg">{event.eventName}</h3>
													</div>
													{event.status && (
														<div className="absolute top-3 right-3">
															<span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold backdrop-blur-sm ${event.status === 'UPCOMING' ? 'bg-green-100/90 text-green-800' :
																event.status === 'ONGOING' ? 'bg-blue-100/90 text-blue-800' :
																	event.status === 'COMPLETED' ? 'bg-gray-100/90 text-gray-800' :
																		'bg-red-100/90 text-red-800'
																}`}>
																{event.status}
															</span>
														</div>
													)}
												</div>

												{/* Event Details */}
												<div className="p-4 space-y-3">
													{event.eventDate && (
														<div className="flex items-center text-sm text-gray-600">
															<svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
															</svg>
															{new Date(event.eventDate).toLocaleDateString('en-IN', {
																year: 'numeric',
																month: 'short',
																day: 'numeric'
															})}
														</div>
													)}
													{event.location && (
														<div className="flex items-center text-sm text-gray-600">
															<svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
															</svg>
															{event.location}
														</div>
													)}
												</div>

												{/* View Indicator */}
												<div className="px-4 pb-4">
													<div className="flex items-center justify-end text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
														<span className="text-sm font-medium">View Details</span>
														<svg className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
														</svg>
													</div>
												</div>
											</div>
										))}
									</div>
								)}
							</div>
						</div>
					)}
				</div>
			</main>

			{/* File Details Modal */}
			{showFileModal && selectedFile && (
				<div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
					<div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
						<div className="p-6 border-b border-gray-100">
							<div className="flex justify-between items-center">
								<h3 className="text-xl font-bold text-gray-900">Document Details</h3>
								<button
									onClick={() => {
										setShowFileModal(false);
										setSelectedFile(null);
									}}
									className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
								>
									<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>
						</div>

						<div className="p-6 space-y-6">
							<div className="flex items-center">
								<div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center mr-4">
									<svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={getFileIcon(selectedFile.contentType)} />
									</svg>
								</div>
								<div>
									<h4 className="text-lg font-semibold text-gray-900">{selectedFile.originalName}</h4>
									<p className="text-sm text-gray-500">
										{selectedFile.fileSize ? `${(selectedFile.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
									</p>
								</div>
							</div>

							<div className="space-y-4">
								<div className="bg-gray-50 rounded-xl p-4">
									<p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Uploaded By</p>
									<p className="text-sm font-medium text-gray-900">{selectedFile.uploadedByName}</p>
								</div>
								<div className="bg-gray-50 rounded-xl p-4">
									<p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Upload Date</p>
									<p className="text-sm font-medium text-gray-900">{formatDateTime(selectedFile.uploadedAt)}</p>
								</div>
								<div className="bg-gray-50 rounded-xl p-4">
									<p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">File Type</p>
									<p className="text-sm font-medium text-gray-900">{selectedFile.contentType || 'Unknown'}</p>
								</div>
								{selectedFile.tags && selectedFile.tags.length > 0 && (
									<div className="bg-gray-50 rounded-xl p-4">
										<p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Tags</p>
										<div className="flex flex-wrap gap-2">
											{selectedFile.tags.map((tag, index) => (
												<span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
													{tag}
												</span>
											))}
										</div>
									</div>
								)}
							</div>

							<div className="flex space-x-3 pt-4">
								<button
									onClick={() => {
										setShowFileModal(false);
										setSelectedFile(null);
									}}
									className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
								>
									Close
								</button>
								<button
									onClick={() => {
										previewFile(selectedFile);
										setShowFileModal(false);
									}}
									className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:shadow-lg transition-all"
								>
									Preview
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Upload Modal */}
			{showUploadModal && (
				<div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
					<div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
						<div className="p-6 border-b border-gray-100">
							<div className="flex justify-between items-center">
								<h3 className="text-xl font-bold text-gray-900">Upload Document</h3>
								<button
									onClick={() => {
										setShowUploadModal(false);
										setSelectedUploadFile(null);
										setTags([]);
										setNewTag('');
									}}
									className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
								>
									<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>
						</div>

						<div className="p-6 space-y-6">
							{/* File Upload Area */}
							<div
								className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${dragActive
									? 'border-blue-500 bg-blue-50'
									: 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
									}`}
								onDragEnter={handleDrag}
								onDragLeave={handleDrag}
								onDragOver={handleDrag}
								onDrop={handleDrop}
							>
								<input
									type="file"
									onChange={(e) => setSelectedUploadFile(e.target.files[0])}
									disabled={uploading}
									className="hidden"
									id="file-input"
								/>
								<label htmlFor="file-input" className="cursor-pointer">
									<div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
										<svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
										</svg>
									</div>
									<p className="text-lg font-semibold text-gray-900 mb-2">
										{selectedUploadFile ? selectedUploadFile.name : 'Drop files here or click to browse'}
									</p>
									<p className="text-sm text-gray-500">PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX up to 100MB</p>
								</label>
							</div>

							{/* Tags */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">Tags (Optional)</label>
								<div className="flex space-x-2 mb-3">
									<input
										type="text"
										value={newTag}
										onChange={(e) => setNewTag(e.target.value)}
										onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
										placeholder="Add a tag..."
										className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
									/>
									<button
										onClick={addTag}
										disabled={!newTag.trim()}
										className="px-4 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
									>
										Add
									</button>
								</div>
								{tags.length > 0 && (
									<div className="flex flex-wrap gap-2">
										{tags.map((tag, index) => (
											<span key={index} className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
												{tag}
												<button
													onClick={() => removeTag(tag)}
													className="ml-2 text-blue-600 hover:text-blue-800"
												>
													<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
													</svg>
												</button>
											</span>
										))}
									</div>
								)}
							</div>

							{/* Upload Progress */}
							{uploading && (
								<div className="bg-blue-50 rounded-xl p-4">
									<ProgressBar progress={uploadProgress} label="Uploading..." showLabel={true} />
								</div>
							)}

							{/* Action Buttons */}
							<div className="flex space-x-3 pt-4">
								<button
									onClick={() => {
										setShowUploadModal(false);
										setSelectedUploadFile(null);
										setTags([]);
										setNewTag('');
									}}
									disabled={uploading}
									className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-colors"
								>
									Cancel
								</button>
								<button
									onClick={() => handleFileUpload(selectedUploadFile)}
									disabled={!selectedUploadFile || uploading}
									className="flex-1 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
								>
									{uploading ? 'Uploading...' : 'Upload File'}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Confirm Dialog */}
			<ConfirmDialog
				isOpen={confirm.isOpen}
				onClose={() => setConfirm({ ...confirm, isOpen: false })}
				onConfirm={confirm.onConfirm || (() => { })}
				title={confirm.title}
				message={confirm.message}
				type={confirm.type}
			/>

			{/* Alert Dialog */}
			<AlertDialog
				isOpen={alert.isOpen}
				onClose={() => setAlert({ ...alert, isOpen: false })}
				title={alert.title}
				message={alert.message}
				type={alert.type}
			/>
		</div>
	);
};

export default InvestorDashboard;

